import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * GuidedTour (in-house, no deps)
 * - Config-driven steps with selectors
 * - Spotlight highlight with dimmed background using huge box-shadow
 * - Clean tooltip with arrow, Skip and Continue
 * - Autoplay once via localStorage; can be started via custom event
 * - Safe to extend for other pages by passing a different steps array
 */
export default function GuidedTour({
  steps = [],
  storageKey = "rl_tour_completed_v1",
  autoStart = true,
  startEvent = "rl_tour_start_home",
}) {
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [placement, setPlacement] = useState("auto");
  const tooltipRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState({ top: -99999, left: -99999 });
  const [tooltipReady, setTooltipReady] = useState(false);

  const current = steps[idx];

  const markCompleted = () => {
    try {
      localStorage.setItem(storageKey, "1");
    } catch {}
  };

  const isCompleted = useMemo(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  }, [storageKey]);

  const findEl = (sel) => {
    if (!sel) return null;
    try {
      return document.querySelector(sel);
    } catch {
      return null;
    }
  };

  const getViewport = () => {
    const vv = window.visualViewport;
    return {
      vw: vv ? vv.width : window.innerWidth,
      vh: vv ? vv.height : window.innerHeight,
      ox: vv ? vv.offsetLeft : 0,
      oy: vv ? vv.offsetTop : 0,
    };
  };

  const clampRectToViewport = (rect) => {
    const { vh } = getViewport();
    const vwLocal = getViewport().vw;
    const top = Math.max(8, rect.top);
    const left = Math.max(8, rect.left);
    const width = Math.min(vwLocal - left - 8, rect.width);
    const height = Math.min(vh - top - 8, rect.height);
    return { top, left, width, height };
  };

  const computeRect = (el, padding = 8) => {
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return clampRectToViewport({
      top: r.top - padding,
      left: r.left - padding,
      width: r.width + padding * 2,
      height: r.height + padding * 2,
    });
  };

  const applyInset = (rect, inset = 0) => {
    if (!rect || !inset) return rect;
    return clampRectToViewport({
      top: rect.top + inset,
      left: rect.left + inset,
      width: Math.max(1, rect.width - inset * 2),
      height: Math.max(1, rect.height - inset * 2),
    });
  };

  const fitRectForViewport = (rect, opts = {}) => {
    if (!rect) return rect;
    const { vh } = getViewport();
    const vwLocal = getViewport().vw;
    const margin = 8;

    let { maxHeightFraction = 0.72, maxWidthFraction = 0.96, focusFractionY = 0.5, focusFractionX = 0.5 } = opts;

    const maxH = Math.floor(vh * maxHeightFraction);
    const maxW = Math.floor(vwLocal * maxWidthFraction);

    const desiredH = Math.min(rect.height, maxH);
    const desiredW = Math.min(rect.width, maxW);

    const focusY = rect.top + rect.height * focusFractionY;
    const focusX = rect.left + rect.width * focusFractionX;

    let top = Math.round(focusY - desiredH / 2);
    let left = Math.round(focusX - desiredW / 2);

    top = Math.max(margin, Math.min(vh - desiredH - margin, top));
    left = Math.max(margin, Math.min(vwLocal - desiredW - margin, left));

    return { top, left, width: desiredW, height: desiredH };
  };

  const computeUnionRect = (selectors = [], padding = 8) => {
    let top = Number.POSITIVE_INFINITY;
    let left = Number.POSITIVE_INFINITY;
    let right = Number.NEGATIVE_INFINITY;
    let bottom = Number.NEGATIVE_INFINITY;

    let found = false;
    selectors.forEach((sel) => {
      const el = findEl(sel);
      if (!el) return;
      const r = el.getBoundingClientRect();
      top = Math.min(top, r.top);
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
      bottom = Math.max(bottom, r.bottom);
      found = true;
    });
    if (!found) return null;
    const width = right - left;
    const height = bottom - top;
    return clampRectToViewport({
      top: top - padding,
      left: left - padding,
      width: width + padding * 2,
      height: height + padding * 2,
    });
  };

  const scrolledStepRef = useRef(null);

  const refreshLayout = () => {
    if (!active) return;
    const step = steps[idx];
    if (!step) return;

    let baseEl = null;
    let rect = null;

    const { vh } = getViewport();
    const vwLocal = getViewport().vw;
    const isMobile = vwLocal < 768;

    const unionSel = isMobile && step.unionMobile ? step.unionMobile : step.unionSelectors;
    const sel = isMobile && step.selectorMobile ? step.selectorMobile : step.selector;

    const paddingVal = isMobile ? (step.paddingMobile ?? step.padding ?? 10) : (step.padding ?? 10);

    if (unionSel && Array.isArray(unionSel) && unionSel.length > 0) {
      rect = computeUnionRect(unionSel, paddingVal);
      if (!rect) {
        if (step.optional) { goNext(); return; }
        stop();
        return;
      }
    } else {
      baseEl = sel ? findEl(sel) : document.body;
      if (!baseEl) {
        if (step.optional) { goNext(); return; }
        stop();
        return;
      }
      rect = computeRect(baseEl, paddingVal);
    }

    // Adjust rect to improve visibility on small screens
    const fitted = fitRectForViewport(rect, step.fitOptions || { maxHeightFraction: 0.78, maxWidthFraction: 0.98 });
    const insetVal = isMobile ? ((step.insetMobile ?? step.inset) ?? 0) : (step.inset ?? 0);
    let insetRect = applyInset(fitted, insetVal);

    // Fullscreen highlight option (extends beyond edges for clean border)
    let highlightRect = insetRect;
    if (step.fullscreen) {
      // Keep the highlight just inside the viewport so the dimming ring remains visible on all edges
      const margin = 2; // px
      highlightRect = { top: margin, left: margin, width: Math.max(1, vwLocal - margin * 2), height: Math.max(1, vh - margin * 2) };
    }

    // Ensure the target area is visible on mobile (scroll into view once per step)
    const bottom = insetRect.top + insetRect.height;
    const needsScroll = insetRect.top < 12 || bottom > vh - 72;
    if (needsScroll && scrolledStepRef.current !== step.id) {
      scrolledStepRef.current = step.id;
      const desiredY = window.scrollY + insetRect.top + insetRect.height / 2 - vh * 0.45;
      window.scrollTo({ top: Math.max(0, desiredY), behavior: "smooth" });
      // Recompute after scroll settles
      setTimeout(refreshLayout, 220);
    }

    setTargetRect(highlightRect);

    // Compute tooltip placement dynamically (use a visible rect within viewport for positioning)
    // Wait one frame to ensure tooltip has mounted and measured correctly
    requestAnimationFrame(() => {
      const tEl = tooltipRef.current;
      if (!tEl || !insetRect) return;
      const { vh } = getViewport();
      const tw = tEl.offsetWidth;
      const th = tEl.offsetHeight;
      const margin = 12;

      const posBase = step.fullscreen ? { top: 0, left: 0, width: vwLocal, height: vh } : insetRect;

      let pos = { top: posBase.top + posBase.height + margin, left: posBase.left + Math.max(0, (posBase.width - tw) / 2) };
      let finalPlacement = "bottom";

      // If not enough room at bottom, try top
      if (insetRect.top + insetRect.height + margin + th > vh && insetRect.top - margin - th > 0) {
        pos = { top: insetRect.top - margin - th, left: insetRect.left + Math.max(0, (insetRect.width - tw) / 2) };
        finalPlacement = "top";
      }
      // If still clipped horizontally, try right then left
      if (pos.left + tw + 8 > vwLocal) {
        if (insetRect.left + insetRect.width + margin + tw < vwLocal) {
          pos = { top: insetRect.top + Math.max(0, (insetRect.height - th) / 2), left: insetRect.left + insetRect.width + margin };
          finalPlacement = "right";
        } else if (insetRect.left - margin - tw > 0) {
          pos = { top: insetRect.top + Math.max(0, (insetRect.height - th) / 2), left: insetRect.left - margin - tw };
          finalPlacement = "left";
        } else {
          pos.left = Math.max(8, vwLocal - tw - 8);
        }
      }

      // Android Chrome bottom toolbar guard to avoid being covered
      const bottomGuard = 64; // px
      const computedTop = Math.max(8, Math.min(vh - th - 12 - (finalPlacement === "bottom" ? bottomGuard : 0), pos.top));

      setTooltipPos({
        top: computedTop,
        left: Math.max(8, Math.min(vwLocal - tw - 8, pos.left)),
      });
      setPlacement(finalPlacement);
      setTooltipReady(true);
    });
  };

  const start = () => {
    if (active) return;
    setIdx(0);
    setActive(true);
  };

  const stop = () => {
    setActive(false);
    markCompleted();
  };

  const goNext = () => {
    const next = idx + 1;
    if (next >= steps.length) {
      stop();
    } else {
      setIdx(next);
    }
  };

  // Body scroll lock while active
  useEffect(() => {
    if (active) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [active]);

  // Autostart once per new visitor
  useEffect(() => {
    if (autoStart && !isCompleted) {
      const t = setTimeout(() => start(), 700);
      return () => clearTimeout(t);
    }
  }, [autoStart, isCompleted]);

  // External start trigger (from Settings)
  useEffect(() => {
    const onStart = () => start();
    window.addEventListener(startEvent, onStart);
    return () => window.removeEventListener(startEvent, onStart);
  }, [startEvent]);

  // Recompute positions on step change / resize / scroll
  useEffect(() => {
    refreshLayout();
    const onResize = () => refreshLayout();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    const id = setInterval(refreshLayout, 250);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      clearInterval(id);
    };
    // eslint-disable-next-line
  }, [active, idx]);

  if (!active || !current || !targetRect) return null;

  const r = targetRect;
  const radius = current.radius ?? 12;

  return (
    <div className="fixed inset-0" style={{ zIndex: 2147483647 }}>

      {/* Click blocker (transparent, prevents interacting with the page) */}
      <div className="absolute inset-0" style={{ background: "transparent" }} />

      {/* Spotlight rectangle with huge box-shadow to dim outside */}
      <div
        className="absolute pointer-events-none transition-all duration-200 ease-out"
        style={{ zIndex: 2147483646,
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          borderRadius: radius,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.6), 0 0 0 2px rgba(56,189,248,0.85)",
        }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute max-w-[360px] rounded-xl shadow-lg bg-slate-900/95 dark:bg-slate-900/95 border border-slate-900 dark:border-slate-700 p-4 text-white dark:text-slate-100 transition-opacity duration-200"
        style={{ top: tooltipPos.top, left: tooltipPos.left, zIndex: 2147483647, paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))", visibility: tooltipReady ? "visible" : "hidden" }}
      >
        <div className="text-sm font-semibold text-white">{current.title}</div>
        {current.description ? (
          <div className="mt-1 text-sm text-white/90 dark:text-slate-300">{current.description}</div>
        ) : null}
        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            className="text-xs px-3 py-2 rounded-md border border-slate-700 text-white hover:bg-slate-800"
            onClick={stop}
          >
            Skip tutorial
          </button>
          <button
            className="text-xs px-3 py-2 rounded-md text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-200/50 dark:focus:ring-teal-400/40 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 dark:bg-teal-700 dark:hover:bg-teal-800 dark:active:bg-teal-900"
            onClick={goNext}
          >
            Continue
          </button>
        </div>

        {/* Arrow */}
        <div
          className="absolute w-3 h-3 bg-slate-900 dark:bg-slate-900 border border-slate-900 dark:border-slate-700 rotate-45"
          style={{
            top:
              placement === "bottom"
                ? -6
                : placement === "top"
                ? undefined
                : "50%",
            bottom: placement === "top" ? -6 : undefined,
            left:
              placement === "left"
                ? undefined
                : "50%",
            right: placement === "left" ? -6 : placement === "right" ? -6 : undefined,
            transform:
              placement === "left" || placement === "right"
                ? "translateY(-50%) rotate(45deg)"
                : "translateX(-50%) rotate(45deg)",
          }}
        />
      </div>
    </div>
  );
}